import React, { createContext, Dispatch, ReactElement, useContext, useEffect, useReducer } from 'react'
import { CodeSetEntry, ICodeSetEntry } from '../../App/MapContextContainer';
import errors from '../../App/errors';
import { PageContext } from '../../App/PageContextContainer';
import loadingNotifications from '../../App/loadingNotifications';
import { IAppliedFilters } from '../../App/AppSettingsContextContainer';

type Props = {
    children: ReactElement
}

export const HabitatsContext = createContext({} as IHabitatsContext);

type ContextAction =
    | { type: 'setAllSearchCriteria', cuttingRealizationPractices: string[], specialFeatureCodes: string[], declarationArrivalMonths: number[] }
    | { type: 'setSpecialFeatureCodesForDropDown', codes: ICodeSetEntry[] }
    | { type: "setInitialized" }
    | { type: "reset" };

function contextReducer(state: IHabitatsState, action: ContextAction): IHabitatsState {
    switch (action.type) {
        case "setInitialized":
            return { ...state, isInitialized: true };
        case "setAllSearchCriteria":
            localStorage.setItem("appliedHabitatFilters", JSON.stringify(action));

            return {
                ...state,
                cuttingRealizationPractices: action.cuttingRealizationPractices,
                specialFeatureCodes: action.specialFeatureCodes,
                declarationArrivalMonths: action.declarationArrivalMonths
            };
        case "setSpecialFeatureCodesForDropDown":
            const mapped: CodeSetEntry[] = action.codes.map(c => {
                return new CodeSetEntry(c.Koodi, c.Selite_FI, c.Selite_SE, c.Selite_EN);
            });
            
            return { ...state, specialFeatureCodesForDropDown: mapped };
        case 'reset':
            localStorage.removeItem("appliedHabitatFilters");

            return { ...initialState, isInitialized: true, specialFeatureCodesForDropDown: state.specialFeatureCodesForDropDown };
        default:
            throw new Error();
    }
}

export interface IHabitatsContext {
    habitatsStateDispatch: Dispatch<ContextAction>,
    habitatsState: IHabitatsState
}

export interface IHabitatsState {
    cuttingRealizationPractices: string[],
    specialFeatureCodes: string[],
    specialFeatureCodesForDropDown?: CodeSetEntry[],
    declarationArrivalMonths: number[],
    isInitialized: boolean
}

const initialState = {
    isInitialized: false, cuttingRealizationPractices: [],
    specialFeatureCodes: [], declarationArrivalMonths: []
} as IHabitatsState;

export default function HabitatsContextContainer(props: Props) {
    const [habitatsState, habitatsStateDispatch] = useReducer(contextReducer, initialState);
    const pageContext = useContext(PageContext);

    useEffect(() => {
        const asyncCall = async () => {
            try {
                pageContext.setLoadingSomething(loadingNotifications.loadingCollectionMaps);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/GetCollectionAndTaxonMapsV2`);

                const collectionsAndTaxons = await response.json();

                habitatsStateDispatch({ type: "setSpecialFeatureCodesForDropDown", codes: collectionsAndTaxons.specialFeatureCodes.filter((sfc: any) => sfc) });
            } catch (error) {
                pageContext.setMapError(errors.specialFeatureCodeMappingsDataLoadingError);
            }
            finally {
                pageContext.resetLoadingSomething(loadingNotifications.loadingCollectionMaps);
            }

            const filtersFromLocalStorage = localStorage.getItem('appliedFilters');
            const customFiltersFromLocalStorage = localStorage.getItem('appliedHabitatFilters');

            if (filtersFromLocalStorage || customFiltersFromLocalStorage) {
                const appliedFilters: IAppliedFilters = JSON.parse(filtersFromLocalStorage || "{}");
                const customAppliedFilters: IHabitatsState = JSON.parse(customFiltersFromLocalStorage || "{}");

                habitatsStateDispatch({
                    type: 'setAllSearchCriteria',
                    specialFeatureCodes: customAppliedFilters.specialFeatureCodes || [],
                    cuttingRealizationPractices: appliedFilters.cuttingRealizationPractices || [],
                    declarationArrivalMonths: appliedFilters.declarationArrivalMonths || []
                });
            }

            habitatsStateDispatch({ type: "setInitialized" });
        }

        asyncCall();
    }, []);

    return (
        <HabitatsContext.Provider value={{ habitatsStateDispatch: habitatsStateDispatch, habitatsState: habitatsState }}>
            {props.children}
        </HabitatsContext.Provider>
    )
}